/* eslint-disable phorest/moment-import */
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { assert } from '@ember/debug';
import { restartableTask, dropTask } from 'ember-concurrency';
import { queryManager } from 'ember-apollo-client';
import * as Sentry from '@sentry/browser';
import { GraphQLWarning } from 'my-phorest/services/error-handler';
import { AppointmentEvent } from 'my-phorest/utils/calendar-events';
import {
  evictAppointmentsFromResourcesCalendar,
  updateEvent,
  addEventsToCalendarCache,
  moveEvent,
} from 'my-phorest/utils/calendar-cache';
import sortAppointments from 'my-phorest/utils/sort-appointments';
import {
  addMoney,
  sumMoneyCollection,
  subtractMoney,
} from 'my-phorest/utils/currency';
import {
  evictAppointments,
  evictBookingHistory,
  evictClientCourseItem,
  evictClientCourses,
  evictClientRecurringServiceSubscriptions,
} from 'my-phorest/utils/graphql';

import appointmentsQuery from 'my-phorest/gql/queries/appointments.graphql';
import updateAppointment from 'my-phorest/gql/mutations/update-appointment.graphql';
import updateAppointmentsPackageOptions from 'my-phorest/gql/mutations/update-appointments-package-options.graphql';
import updateServiceToPackage from 'my-phorest/gql/mutations/update-service-to-package.graphql';
import deleteAppointments from 'my-phorest/gql/mutations/delete-appointments.graphql';
import swapServiceGroupToService from 'my-phorest/gql/mutations/swap-service-group-to-service.graphql';
import { sendCalendarSynchronizationIssueToSentry } from 'my-phorest/utils/calendar';
import * as calendarCache from 'my-phorest/utils/calendar-cache';

import { variation } from 'ember-launch-darkly';

const SUPPORTED_SCREENS = [
  'appointment-details',
  'client-create',
  'clients-list',
  'services-list',
  'booking-history',
  'recurrence-details',
  'change-staff',
];
const INITIAL_SCREEN = 'clients-list';

export default class AppointmentSlideOverService extends Service {
  @service access;
  @service appointmentTasks;
  @service clientSearch;
  @service errorHandler;
  @service fullCalendar;
  @service intl;
  @service notifications;

  // Data
  @tracked appointments = [];
  @tracked client = null;
  @tracked displayDate = null;
  @tracked showOnlyBookingsWithNotes = false;
  @tracked staffMember = null;
  @tracked room = null;
  @tracked machine = null;

  // Flow controls
  @tracked isOpen = false;
  @tracked screen = INITIAL_SCREEN;

  @queryManager apollo;

  close() {
    this.isOpen = false;
    this.resetState();

    if (this.fullCalendar) {
      requestAnimationFrame(() => {
        this.fullCalendar.calendarApi?.updateSize();
      });
    }
  }

  get date() {
    if (!Array.isArray(this.appointments) || this.appointments.length === 0)
      return this.displayDate ?? new Date();

    const earliestAppointment = [...this.appointments].sort(
      sortAppointments
    )[0];
    return new Date(
      `${earliestAppointment.date}T${earliestAppointment.startTime}Z`
    );
  }

  /**
   * Opens AppointmentSlideOver and hydrates it with provided data. Please notice that not all data is strictly needed.
   * Some of the arguments are optional for opening.
   *
   * @param {array} [appointments]
   * @param {object} [client=null]
   * @param {date} date
   * @param {string} [screen]
   * @param {object} [staffMember]
   * @param {object} [room]
   * @param {object} [machine]
   */
  open({
    appointments,
    client = null,
    date,
    screen,
    staffMember,
    room,
    machine,
  }) {
    if (Array.isArray(appointments)) {
      this.appointments = appointments;

      this.reFetchAppointmentsByIdsTask.perform(
        appointments.map((appt) => appt.id)
      );
    }

    if (screen) {
      this.setScreen.perform(screen);
    }

    if (date) {
      this.displayDate = date;
    }

    this.client = client;
    this.staffMember = staffMember;
    this.room = room;
    this.machine = machine;
    this.isOpen = true;

    if (variation('release-auto-scroll-to-staff-member')) {
      if (this.fullCalendar.isStaffView && staffMember?.id) {
        this.scrollToResource('Staff', staffMember.id);
      } else if (this.fullCalendar.isRoomView && room?.id) {
        this.scrollToResource('Room', room.id);
      } else if (this.fullCalendar.isMachineView && machine?.id) {
        this.scrollToResource('Machine', machine.id);
      }
    }
  }

  scrollToResource(resourceType, id) {
    const domElement = document.querySelector(
      `[data-resource-id="${resourceType}::${id}"]`
    );

    if (domElement) {
      setTimeout(() => {
        domElement.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }

  unpaidAppointments(appointments) {
    return appointments.filter((appointment) => appointment.state !== 'PAID');
  }

  hasUnpaidAppointments(appointments) {
    return this.unpaidAppointments(appointments).length > 0;
  }

  hasPaidAppointments(appointments) {
    return appointments.some((appointment) => appointment.state === 'PAID');
  }

  hasCheckedInAppointments(appointments) {
    return appointments.some(
      (appointment) => appointment.state === 'CHECKED_IN'
    );
  }

  totalPrice(appointments) {
    return sumMoneyCollection(
      this.unpaidAppointments(appointments),
      'priceMonetary'
    );
  }

  priceSubtotal(appointments) {
    if (appointments.length > 0) {
      const subtotal = subtractMoney(
        this.totalPrice(appointments),
        this.depositSubtotal(appointments)
      );
      return {
        amount: subtotal,
        currency: appointments[0].priceMonetary.currency,
      };
    } else {
      return 0;
    }
  }

  inHouseDepositAmount(appointments) {
    return sumMoneyCollection(
      this.unpaidAppointments(appointments),
      'depositAmountMonetary'
    );
  }

  onlineDepositAmount(appointments) {
    return sumMoneyCollection(
      this.unpaidAppointments(appointments),
      'depositPaidMonetary'
    );
  }

  depositSubtotal(appointments) {
    return addMoney(
      this.inHouseDepositAmount(appointments),
      this.onlineDepositAmount(appointments)
    );
  }

  removeAppointmentsFromTrackedData(appointmentIds) {
    let appointmentIdsToRemove = appointmentIds
      .map((id) => this._findAppointmentAndIndexById(id).appointment)
      .filter(Boolean)
      .map((appointment) => appointment.id);

    this.appointments = this.appointments.filter(
      (cachedAppointment) =>
        !appointmentIdsToRemove.includes(cachedAppointment.id)
    );
  }

  @restartableTask
  *reFetchAppointmentsByIdsTask(ids) {
    if (ids.length === 0) {
      Sentry.withScope(function (scope) {
        scope.setLevel('warning');
        const error = new GraphQLWarning("didn't receive ids");
        error.name += ' | reFetchAppointmentsByIdsTask';

        Sentry.captureException(error);
      });
      return;
    }

    try {
      let { appointments } = yield this.apollo.query({
        query: appointmentsQuery,
        variables: {
          filterBy: {
            ids,
          },
        },
        fetchPolicy: 'network-only',
      });

      if (appointments) {
        appointments = [...appointments.edges.map((edge) => edge.node)]
          .sort(sortAppointments)
          .map((appointment) => new AppointmentEvent(appointment));

        appointments.forEach((appointment) => {
          this.upsertAppointmentInTrackedData(appointment);
        });
      }
    } catch (error) {
      // Fail gracefully. A lot of data is already loaded through calendar.
      // It's probably better to keep slideover working (without an error popping up)
      // as most of the functionality doesn't rely on this re-fetch of data.
      this.errorHandler.handle(error, { showError: false });
    }
  }

  @restartableTask
  *setScreen(name) {
    assert(
      `Appointment screen "${name}" is not supported`,
      SUPPORTED_SCREENS.includes(name)
    );

    if (name === 'booking-history') {
      const accessAllowed = yield this.access.requirePermission(
        'clients.view-service-history'
      );
      if (!accessAllowed) {
        return;
      }
    }
    yield (this.screen = name);
  }

  updateClient(client) {
    this.client = client;
  }

  upsertAppointmentInTrackedData(appointment, updateClient) {
    if (!updateClient && this.client?.id !== appointment.clientId) {
      // This method is often called after async action. It can be called for a different state of the appointment slideover.
      // In that case, we are not interested in updating anything.
      return;
    }

    if (updateClient) {
      this.client = appointment.client;
    }

    let { appointment: cachedAppointment, index } =
      this._findAppointmentAndIndexById(appointment.id);

    if (cachedAppointment) {
      let newAppointment = new AppointmentEvent(cachedAppointment);
      for (const property in appointment) {
        if (typeof appointment[property] !== 'undefined') {
          newAppointment[property] = appointment[property];
        }
      }

      this.appointments = [
        ...this.appointments.slice(0, index),
        newAppointment,
        ...this.appointments.slice(index + 1),
      ];
    } else {
      this.appointments = [...this.appointments, appointment];
    }
  }

  /**
   * Updates appointments in the slide over when a client's completion status changes.
   * This method fetches updated appointment data and updates the tracked appointments
   * that are currently visible in the slide over.
   *
   * @param {string} clientId - The ID of the client whose completion status changed
   * @param {Array} appointmentIds - Array of appointment IDs to update
   * @param {QueryManager} apollo - The Apollo query manager
   * @returns {Promise<void>}
   */
  async updateAppointmentsAfterClientStatusChange(
    clientId,
    appointmentIds,
    apollo
  ) {
    if (!appointmentIds || appointmentIds.length === 0) {
      return;
    }

    try {
      // Get updated appointment data from the cache
      const { appointments } = await apollo.query({
        query: appointmentsQuery,
        variables: {
          filterBy: { ids: appointmentIds },
        },
        fetchPolicy: 'network-only',
      });

      const updatedAppointments = appointments.edges.map((edge) => edge.node);

      // Get currently visible appointments in the slide over
      const visibleSlideOverAppointments = this.appointments.map(
        (appt) => appt.id
      );

      // Filter appointments that need to be updated in the slide over
      const appointmentsToUpdate = updatedAppointments.filter((appt) =>
        visibleSlideOverAppointments.includes(appt.id)
      );

      // Update each appointment in the tracked data
      appointmentsToUpdate.forEach((appointment) => {
        this.upsertAppointmentInTrackedData(appointment, clientId);
      });
    } catch (error) {
      console.warn(
        'Failed to update appointments after client status change:',
        error
      );
    }
  }

  _findAppointmentAndIndexById(appointmentId) {
    let appointment = this.appointments.find(
      (appointment) => appointment.id === appointmentId
    );

    if (appointment) {
      let index = this.appointments.indexOf(appointment);
      return { appointment, index };
    }

    return { appointment: null, index: -1 };
  }

  resetState() {
    this.appointments = [];
    this.client = null;
    this.displayDate = null;
    this.showOnlyBookingsWithNotes = false;
    this.staffMember = null;
    this.screen = INITIAL_SCREEN;
    this.clientSearch.reset();
  }

  @dropTask
  *updateAppointmentTask(
    variables = {},
    { previousMachineId, previousRoomId, previousStaffMemberId } = {}
  ) {
    try {
      const response = yield this.apollo.mutate({
        mutation: updateAppointment,
        variables,
        update: (cache, { data: { updateAppointment } }) => {
          evictBookingHistory(cache);
          const { appointment } = updateAppointment;
          if (appointment) {
            updateEvent(cache, {
              date: appointment.date,
              eventId: appointment.id,
              resourceId: appointment.staffMemberId,
            });

            if (
              previousStaffMemberId &&
              previousStaffMemberId !== appointment.staffMemberId
            ) {
              moveEvent(cache, {
                fromDate: appointment.date,
                toDate: appointment.date,
                eventId: appointment.id,
                fromResourceId: previousStaffMemberId,
                toResourceId: appointment.staffMemberId,
              });
            }

            if (
              previousMachineId &&
              previousMachineId !== appointment.machine?.id
            ) {
              moveEvent(cache, {
                fromDate: appointment.date,
                toDate: appointment.date,
                eventId: appointment.id,
                fromResourceId: previousMachineId,
                toResourceId: appointment.machine?.id,
                resourceType: 'Machine',
              });
            }

            if (previousRoomId && previousRoomId !== appointment.room?.id) {
              moveEvent(cache, {
                fromDate: appointment.date,
                toDate: appointment.date,
                eventId: appointment.id,
                fromResourceId: previousRoomId,
                toResourceId: appointment.room?.id,
                resourceType: 'Room',
              });
            }

            this.upsertAppointmentInTrackedData(
              new AppointmentEvent(appointment)
            );

            if (appointment.clientCourseItem) {
              let { id: clientCourseItemId } = appointment.clientCourseItem;

              evictClientCourseItem(cache, clientCourseItemId);
            }
          }

          cache.gc();
        },
      });

      return response.updateAppointment;
    } catch (e) {
      let errorCode = e?.errors?.[0]?.extensions?.code;

      if (errorCode === 'ResourceNotFoundError') {
        this.appointmentTasks.handleDeletedAppointmentUpdateAttempt();
        this.removeAppointmentsFromTrackedData([variables.id]);
        return null;
      } else {
        throw e;
      }
    }
  }

  @dropTask
  *updatePackageTask(variables = {}) {
    const response = yield this.apollo.mutate({
      mutation: updateAppointmentsPackageOptions,
      variables,
      update: (cache, { data: { updateAppointments } }) => {
        evictBookingHistory(cache);
        if (updateAppointments.appointments) {
          updateAppointments.appointments.forEach((appointment) => {
            updateEvent(cache, {
              date: appointment.date,
              eventId: appointment.id,
              resourceId: appointment.staffMemberId,
            });
          });

          cache.gc();
        }
      },
    });

    return response.updateAppointments;
  }

  @dropTask
  *updateServiceToPackageTask(editedAppointment, variables = {}) {
    const response = yield this.apollo.mutate({
      mutation: updateServiceToPackage,
      variables,
      update: (
        cache,
        { data: { updateServiceAppointmentToServiceGroupAppointment } }
      ) => {
        if (updateServiceAppointmentToServiceGroupAppointment.appointments) {
          evictAppointments(cache, [editedAppointment.id]);
          evictAppointmentsFromResourcesCalendar(cache, [editedAppointment]);
          evictBookingHistory(cache);
          evictClientCourses(cache, editedAppointment.client.id);
          evictClientRecurringServiceSubscriptions(
            cache,
            editedAppointment.client.id
          );
          addEventsToCalendarCache(
            cache,
            updateServiceAppointmentToServiceGroupAppointment.appointments
          );
          this.removeAppointmentsFromTrackedData([editedAppointment.id]);

          cache.gc();
        }
      },
    });
    return response.updateServiceAppointmentToServiceGroupAppointment;
  }

  @dropTask
  *deleteAppointmentsTask(appointments) {
    let appointmentsIds = appointments.map((appointment) => appointment.id);

    try {
      const response = yield this.apollo.mutate({
        mutation: deleteAppointments,
        variables: { ids: appointmentsIds },
        update: () => {
          this.appointmentTasks.updateCacheAfterAppointmentRemoval(
            appointments
          );
        },
      });
      return response.deleteAppointments;
    } catch (e) {
      let errorCode = e?.errors?.[0]?.extensions?.validationCode;

      if (
        errorCode === 'APPOINTMENT_DELETED' ||
        errorCode === 'APPOINTMENT_CANCELLED'
      ) {
        this.appointmentTasks.removePreviouslyDeletedAppointments(appointments);

        if (errorCode === 'APPOINTMENT_CANCELLED') {
          this.notifications.info(
            this.intl.t(
              'appointments.synchronization.appointment-already-cancelled.title'
            ),
            this.intl.t(
              'appointments.synchronization.appointment-already-cancelled.description'
            ),
            { sticky: true }
          );
        }
      } else if (errorCode === 'APPOINTMENT_ALREADY_PAID') {
        sendCalendarSynchronizationIssueToSentry(
          "can't remove already paid appointment"
        );
        calendarCache.invalidate(this.apollo.apollo.client.cache);
        this.notifications.info(
          this.intl.t(
            'appointments.synchronization.appointment-already-paid.title'
          ),
          this.intl.t(
            'appointments.synchronization.appointment-already-paid.description'
          ),
          { sticky: true }
        );
      } else {
        throw e;
      }
    }
  }

  preserveAppointmentDateTime({ date, startTime }) {
    this.displayDate = new Date(`${date}T${startTime}Z`);
  }

  get state() {
    return {
      appointments: this.appointments,
      client: this.client,
      date: this.date,
      screen: this.screen,
      showOnlyBookingsWithNotes: this.showOnlyBookingsWithNotes,
      staffMember: this.staffMember,
    };
  }

  @dropTask
  *swapPackageToService(input) {
    return yield this.apollo.mutate(
      {
        mutation: swapServiceGroupToService,
        variables: { input },
      },
      'swapServiceGroupToService'
    );
  }
}
